// CheckboxOption.tsx
import { Option } from "@/types/productTypes";
import { Box, VStack, Heading, useCheckboxGroup } from "@chakra-ui/react";
import { useOptionConfig } from "../../hooks/useOptionConfig";
import CheckboxItem from "./CheckboxItem";
import useBreakpoint from "@/hooks/use-breakpoint";
import { useTranslation } from "@/hooks/use-translation";

type CheckboxOptionProps = {
   option: Option;
};

export default function CheckboxOption({ option }: CheckboxOptionProps) {
   const { isMobile } = useBreakpoint();
   const { translate } = useTranslation();
   // Provide a default array of selected items if none is set
   const defaultValue = {
      selectedItem:
         (option.items?.length &&
            option.defaultValue &&
            option.defaultValue.toString()) ||
         ([] as string[]),
      selected: true,
   };

   const { fieldValue, handleChange } = useOptionConfig(option, defaultValue);

   // Convert the current fieldValue into an array of checked item IDs
   const checkedValues = Array.isArray(fieldValue?.selectedItem)
      ? fieldValue.selectedItem
      : [];

   // Chakra UI's useCheckboxGroup for easier multi-select
   const { getCheckboxProps } = useCheckboxGroup({
      value: checkedValues,
      onChange: (values: string[]) => {
         // Use our hook's handleChange to sync with react-hook-form
         handleChange(values);
      },
   });

   return (
      <Box mb="1.5rem">
         <Heading
            as="h3"
            fontSize={isMobile ? "0.75rem" : "1rem"}
            fontWeight="600"
            textTransform="uppercase"
            color="gray.700"
            mb="0.5rem"
         >
            {translate(option)}
         </Heading>
         <VStack align="stretch" spacing={4}>
            {option.items?.map((item, index) => {
               const checkbox = getCheckboxProps({ value: item.id });
               return (
                  <CheckboxItem
                     key={index}
                     item={item}
                     controlValue={checkedValues}
                     checkbox={checkbox}
                  />
               );
            })}
         </VStack>
      </Box>
   );
}
